import { createContext, useContext, useEffect, useState } from "react";
import { api, getUser, setSession, clearSession } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => getUser());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Refresh the local user from the server so email verification /
    // token version / role changes reflect immediately. CRITICAL: this
    // must NOT log the customer out on transient failures — a wifi drop
    // or 502/504 blip while the app is loading would otherwise wipe a
    // perfectly valid session and force a login prompt.
    //
    // Definitive auth rejection → clear session:
    //   * HTTP 401 (unauthenticated / bad token)
    //   * HTTP 403 with `detail.code === "auth_invalid"` (explicit
    //     server-side revoke; matches the reserved code we control).
    //
    // Anything else → preserve the current local session unchanged:
    //   * no `err.response` at all (network / DNS / CORS / timeout /
    //     aborted request)
    //   * HTTP 5xx (server temporarily unavailable)
    //   * any other 4xx that is NOT an auth rejection.
    const token = localStorage.getItem("djspy_token");
    if (!token || !user) return;
    api.get("/auth/me").then((r) => {
      setUser(r.data);
      localStorage.setItem("djspy_user", JSON.stringify(r.data));
    }).catch((err) => {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const authInvalidCode =
        (typeof detail === "object" && detail?.code === "auth_invalid") ||
        (typeof detail === "string" && detail === "auth_invalid");
      const isDefinitiveAuthRejection =
        status === 401 || (status === 403 && authInvalidCode);
      if (isDefinitiveAuthRejection) {
        clearSession();
        setUser(null);
      }
      // else: preserve the local session. Optionally we could surface a
      // subtle offline indicator, but silently keeping the customer
      // signed in on transient failures is the correct default.
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for verify-email refresh so the banner disappears immediately.
  // Also refresh on focus / visibilitychange / cross-tab storage writes so
  // an original logged-in tab self-heals when the customer verifies via
  // another tab, another browser, or an email-client webview.
  useEffect(() => {
    const applyStored = () => {
      const stored = localStorage.getItem("djspy_user");
      if (stored) {
        try { setUser(JSON.parse(stored)); } catch (_) {}
      }
    };
    let lastFetchAt = 0;
    const refetchMe = async () => {
      const token = localStorage.getItem("djspy_token");
      if (!token) return;
      // Throttle to at most once every 5s to avoid rapid-fire refetches
      // when the tab bounces between focus/visible.
      const now = Date.now();
      if (now - lastFetchAt < 5000) return;
      lastFetchAt = now;
      try {
        const r = await api.get("/auth/me");
        if (r?.data) {
          setUser(r.data);
          localStorage.setItem("djspy_user", JSON.stringify(r.data));
        }
      } catch (_) {
        // Preserve current session on transient failures — mirrors the
        // mount-time policy above.
      }
    };
    const onCustom = () => applyStored();
    const onStorage = (e) => {
      if (e.key === "djspy_user" && e.newValue) applyStored();
    };
    const onFocus = () => { refetchMe(); };
    const onVisibility = () => {
      if (document.visibilityState === "visible") refetchMe();
    };
    window.addEventListener("djspy-user-refreshed", onCustom);
    window.addEventListener("storage", onStorage);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("djspy-user-refreshed", onCustom);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const login = async (email, password) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setSession(data.token, data.user);
      setUser(data.user);
      return data.user;
    } finally { setLoading(false); }
  };

  const signup = async (email, password, name) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/signup", { email, password, name });
      setSession(data.token, data.user);
      setUser(data.user);
      return data.user;
    } finally { setLoading(false); }
  };

  const logout = () => { clearSession(); setUser(null); };

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
